import { sumMoney, toMoneyNumber } from '@erp/shared-utils';
import { AP_DEMO_IDS } from '@/demo/ap/fixture-seed';
import { COMPANY, getPerson } from '../../data/company';
import { daysAgo } from '../../data/dates';
import type { ContractId, SupplierId } from '../../data/types';

/**
 * The fourteen invoices in the compliance queue: the ten README invoices (the
 * open queue) plus the four invoices of the Accounts Payable demo, which mirror
 * the AP fixture (`@/demo/ap/fixture-seed`) and link to the real AP invoice.
 * Dates are relative (`daysAgo`) so the queue stays fresh.
 *
 * Each invoice carries what the engine needs to check it: the billed lines,
 * the purchase order and receipt it should match, and a few "facts" taken
 * from other systems (uptime measurements, YTD purchases, cumulative billing).
 */

export const INVOICE_IDS = [
  'CV-2026-08812',
  'LB-26-3391',
  'HP-INV-55102',
  'TCF-778120',
  'STR-INV-20931',
  'KS-104477',
  'NGF-22871',
  'GAI-2026-0912',
  'AFP-90233',
  'BLC-3318',
  // Accounts Payable demo invoices (see `apInvoiceId`).
  'AIG-90412',
  'BS-22188',
  'BVL-7741',
  'SF-3398',
] as const;

export type InvoiceId = (typeof INVOICE_IDS)[number];

export function isInvoiceId(value: string): value is InvoiceId {
  return INVOICE_IDS.some((id) => id === value);
}

export type LineKind =
  | 'goods'
  | 'freight'
  | 'duty'
  | 'labor'
  | 'expense'
  | 'line_haul'
  | 'fuel_surcharge'
  | 'accessorial'
  | 'overtime'
  | 'subscription'
  | 'milestone'
  | 'fee';

/** Supporting document the agreement requires before a charge is payable. */
export interface LineEvidence {
  kind: 'signed_proof' | 'written_approval' | 'milestone_acceptance';
  onFile: boolean;
  /** Reference of the proof on file, e.g. an acceptance memo number. */
  ref?: string;
}

export interface InvoiceLine {
  id: string;
  description: string;
  kind: LineKind;
  quantity: number;
  unit: string;
  unitPrice: number;
  /** quantity x unitPrice, to the cent. */
  amount: number;
  /** PO line this line bills against. Missing means the line is not on the PO. */
  poLine?: string;
  /** Role as billed (labor lines). */
  role?: string;
  /** Role as logged on the approved timesheet, when it differs from `role`. */
  approvedRole?: string;
  /** Worker pay rate per hour (labor lines under a markup cap). */
  payRate?: number;
  evidence?: LineEvidence;
  /** Itemised detail printed under the line on the document. */
  subItems?: { description: string; amount: number }[];
  /** Small second line under the description on the document. */
  detail?: string;
}

export interface PoLine {
  id: string;
  description: string;
  quantity: number;
  unit: string;
  unitPrice: number;
}

export interface PurchaseOrderRef {
  id: string;
  lines: PoLine[];
}

export interface Receipt {
  id: string;
  kind:
    | 'Goods receipt'
    | 'Service receipt'
    | 'Approved timesheets'
    | 'Proof of delivery'
    | 'Milestone acceptance';
  date: Date;
  location: string;
  /** Quantity received or approved per invoice line id. */
  quantities: Record<string, number>;
}

export interface InvoiceFacts {
  /** Measured service availability for the billing period. */
  uptime?: { period: string; pct: number };
  /** Credits the supplier already applied on this invoice. */
  creditsApplied?: number;
  /** YTD purchases used for rebate tiers. */
  rebate?: {
    ytdNetPurchases: number;
    creditedYtd: number;
    /** `all_purchases`: the tier is retroactive to the first dollar. */
    basis: 'above_threshold' | 'all_purchases';
  };
  /** Billed against a budget-cap clause before this invoice, by clause id. */
  cumulativeBilled?: Record<string, number>;
}

export type InvoiceChannel = 'E-invoice' | 'Email (PDF)' | 'Supplier portal';

export interface Invoice {
  id: InvoiceId;
  supplierId: SupplierId;
  /** Agreements checked, governing agreement first. */
  contractIds: ContractId[];
  channel: InvoiceChannel;
  /** Invoice date. Also the date Pillar received it. */
  invoiceDate: Date;
  /** Terms as printed on the invoice. */
  statedTerms: {
    label: string;
    netDays: number;
    discount?: { pct: number; days: number };
  };
  /** Service period or delivery reference printed on the document. */
  reference?: string;
  shipTo: string;
  letterhead: { address: string[]; arEmail: string };
  lines: InvoiceLine[];
  po: PurchaseOrderRef;
  receipt: Receipt;
  facts: InvoiceFacts;
  /**
   * Id of the same invoice on the Accounts Payable demo (the real AP invoice
   * page and slide-in). Only the four AP-demo invoices have one.
   */
  apInvoiceId?: string;
}

/** Invoice total, summed exactly. */
export function invoiceTotal(invoice: Invoice): number {
  return toMoneyNumber(sumMoney(invoice.lines.map((line) => line.amount)));
}

export const BILL_TO = {
  name: COMPANY.legalName,
  lines: ['Attn: Accounts Payable', 'Dallas, TX'],
};

const DAL = 'Dallas DC (DAL)';
const PHX = 'Phoenix DC (PHX)';
const HAY = 'Hayward DC (HAY)';
const HQ = 'Dallas headquarters (HQ)';

/** Approvers printed on receipts, taken from the shared cast. */
const REYES = getPerson('daniel-reyes').name;
const O_NEILL = getPerson('kevin-oneill').name;
const HALE = getPerson('marcus-hale').name;
const BROOKS = getPerson('lauren-brooks').name;

export const INVOICES: Record<InvoiceId, Invoice> = {
  'CV-2026-08812': {
    id: 'CV-2026-08812',
    supplierId: 'sup-corvane',
    contractIds: ['SA-CORV-2024-017'],
    channel: 'E-invoice',
    invoiceDate: daysAgo(2),
    statedTerms: { label: 'Net 30', netDays: 30 },
    reference: 'Delivery note DN-77418',
    shipTo: DAL,
    letterhead: {
      address: [
        '1200 Millrace Road',
        'Carrollton, GA 30117',
        'Federal ID 58-3318442',
      ],
      arEmail: 'ar@corvane.example',
    },
    lines: [
      {
        id: 'L1',
        description:
          'Copper building wire, 1/0 gauge, 500 ft reels, lot CV-W-26-0417',
        kind: 'goods',
        quantity: 700,
        unit: 'C',
        unitPrice: 431.5,
        amount: 302_050,
        poLine: '10',
        detail: '140 reels of 500 ft, 70,000 ft in total',
      },
      {
        id: 'L2',
        description: 'Freight and handling',
        kind: 'freight',
        quantity: 1,
        unit: 'lot',
        unitPrice: 3_850,
        amount: 3_850,
      },
    ],
    po: {
      id: 'PO-4500213',
      lines: [
        {
          id: '10',
          description: 'Copper building wire, 1/0 gauge, 500 ft reels',
          quantity: 700,
          unit: 'C',
          unitPrice: 424.36,
        },
      ],
    },
    receipt: {
      id: 'GR-5100872',
      kind: 'Goods receipt',
      date: daysAgo(9),
      location: 'Dallas DC dock 2',
      quantities: { L1: 700 },
    },
    facts: {
      rebate: {
        ytdNetPurchases: 24_380_000,
        creditedYtd: 0,
        basis: 'above_threshold',
      },
    },
  },
  'LB-26-3391': {
    id: 'LB-26-3391',
    supplierId: 'sup-lumen',
    contractIds: ['SA-LUMN-2025-002'],
    channel: 'Email (PDF)',
    invoiceDate: daysAgo(4),
    statedTerms: { label: 'Net 60', netDays: 60 },
    reference: 'Bill of lading NGB-HAY-26-0931',
    shipTo: HAY,
    letterhead: {
      address: [
        'No. 88 Qianjiang Industrial Road, Zhenhai District',
        'Ningbo, Zhejiang 315200, China',
        'USCC 91330201MA2AXK7N4B',
      ],
      arEmail: 'ar@lumen-drive.example',
    },
    lines: [
      {
        id: 'L1',
        description: 'Motor drive, 50 HP, 480 V, UL listed, lot LD-50-26-0412',
        kind: 'goods',
        quantity: 60,
        unit: 'each',
        unitPrice: 5_500,
        amount: 330_000,
        poLine: '10',
      },
      {
        id: 'L2',
        description: 'Tariff surcharge 25%',
        kind: 'duty',
        quantity: 1,
        unit: 'lot',
        unitPrice: 82_500,
        amount: 82_500,
        detail: 'US import duties on the goods value, passed through',
      },
    ],
    po: {
      id: 'PO-4500209',
      lines: [
        {
          id: '10',
          description: 'Motor drive, 50 HP, 480 V',
          quantity: 60,
          unit: 'each',
          unitPrice: 5_500,
        },
      ],
    },
    receipt: {
      id: 'GR-5100861',
      kind: 'Goods receipt',
      date: daysAgo(12),
      location: 'Hayward DC receiving',
      quantities: { L1: 60 },
    },
    facts: {},
  },
  'HP-INV-55102': {
    id: 'HP-INV-55102',
    supplierId: 'sup-halbrook',
    contractIds: ['SOW-HALB-2026-03', 'MSA-HALB-2023-011'],
    channel: 'Supplier portal',
    invoiceDate: daysAgo(6),
    statedTerms: { label: 'Net 60', netDays: 60 },
    reference: 'Tariff & Network Resilience Study, September 2026',
    shipTo: HQ,
    letterhead: {
      address: [
        '100 Atlantic Ridge, Suite 2100',
        'Boston, MA 02110',
        'Federal ID 04-3318827',
      ],
      arEmail: 'billing@halbrook.example',
    },
    lines: [
      {
        id: 'L1',
        description: 'Partner',
        kind: 'labor',
        quantity: 12,
        unit: 'h',
        unitPrice: 950,
        amount: 11_400,
        poLine: 'P1',
        role: 'Partner',
      },
      {
        id: 'L2',
        description: 'Senior Partner',
        kind: 'labor',
        quantity: 22,
        unit: 'h',
        unitPrice: 1_150,
        amount: 25_300,
        poLine: 'P1',
        role: 'Senior Partner',
        approvedRole: 'Partner',
        detail: 'Tariff scenario modelling and steering committee',
      },
      {
        id: 'L3',
        description: 'Principal',
        kind: 'labor',
        quantity: 18,
        unit: 'h',
        unitPrice: 750,
        amount: 13_500,
        poLine: 'PR1',
        role: 'Principal',
      },
      {
        id: 'L4',
        description: 'Consultant',
        kind: 'labor',
        quantity: 45,
        unit: 'h',
        unitPrice: 420,
        amount: 18_900,
        poLine: 'C1',
        role: 'Consultant',
      },
      {
        id: 'L5',
        description: 'Analyst',
        kind: 'labor',
        quantity: 55,
        unit: 'h',
        unitPrice: 280,
        amount: 15_400,
        poLine: 'A1',
        role: 'Analyst',
      },
      {
        id: 'L6',
        description: 'Reimbursable expenses (receipts attached)',
        kind: 'expense',
        quantity: 1,
        unit: 'lot',
        unitPrice: 11_830,
        amount: 11_830,
        poLine: 'E1',
        subItems: [
          {
            description: 'Airfare and lodging, DC and branch visits',
            amount: 7_420,
          },
          { description: 'Ground transportation', amount: 1_860 },
          {
            description: 'Trade-data and research subscriptions',
            amount: 2_550,
          },
        ],
      },
    ],
    po: {
      id: 'PO-4500188',
      lines: [
        {
          id: 'P1',
          description: 'Partner',
          quantity: 60,
          unit: 'h',
          unitPrice: 950,
        },
        {
          id: 'PR1',
          description: 'Principal',
          quantity: 40,
          unit: 'h',
          unitPrice: 750,
        },
        {
          id: 'C1',
          description: 'Consultant',
          quantity: 80,
          unit: 'h',
          unitPrice: 420,
        },
        {
          id: 'A1',
          description: 'Analyst',
          quantity: 100,
          unit: 'h',
          unitPrice: 280,
        },
        {
          id: 'E1',
          description: 'Expenses (10% of fees)',
          quantity: 1,
          unit: 'lot',
          unitPrice: 8_450,
        },
      ],
    },
    receipt: {
      id: 'TS-2026-09',
      kind: 'Approved timesheets',
      date: daysAgo(8),
      location: `Approved by ${REYES}`,
      quantities: { L1: 12, L2: 22, L3: 18, L4: 45, L5: 55, L6: 1 },
    },
    facts: {
      cumulativeBilled: { 'SOW-HALB-2026-03:5.2': 371_870 },
    },
  },
  'TCF-778120': {
    id: 'TCF-778120',
    supplierId: 'sup-transcon',
    contractIds: ['MSA-TRAN-2025-006'],
    channel: 'E-invoice',
    invoiceDate: daysAgo(3),
    statedTerms: { label: 'Net 45', netDays: 45 },
    reference: 'Weekly freight bill, 12 loads',
    shipTo: PHX,
    letterhead: {
      address: [
        '4100 Distribution Parkway',
        'Memphis, TN 38118',
        'MC 773920 / SCAC TCFC',
      ],
      arEmail: 'ar@transcontinental-freight.example',
    },
    lines: [
      {
        id: 'L1',
        description:
          'Base freight, Memphis, TN to Phoenix, AZ (enclosed trailer, full truckload)',
        kind: 'line_haul',
        quantity: 8,
        unit: 'load',
        unitPrice: 4_100,
        amount: 32_800,
        poLine: '10',
      },
      {
        id: 'L2',
        description:
          'Base freight, Memphis, TN to Phoenix, AZ (part truckload, consolidated)',
        kind: 'line_haul',
        quantity: 4,
        unit: 'load',
        unitPrice: 3_800,
        amount: 15_200,
        poLine: '20',
      },
      {
        id: 'L3',
        description: 'Fuel surcharge, 24% of base freight',
        kind: 'fuel_surcharge',
        quantity: 1,
        unit: 'lot',
        unitPrice: 11_520,
        amount: 11_520,
        poLine: '30',
      },
      {
        id: 'L4',
        description: 'Waiting time, Phoenix DC dock 3, load RT-40571',
        kind: 'accessorial',
        quantity: 4.6,
        unit: 'h',
        unitPrice: 250,
        amount: 1_150,
        evidence: { kind: 'signed_proof', onFile: false },
        detail: '4.6 h beyond the 2 h free period',
      },
    ],
    po: {
      id: 'PO-4500221',
      lines: [
        {
          id: '10',
          description: 'Base freight, Memphis to Phoenix, full truckload',
          quantity: 8,
          unit: 'load',
          unitPrice: 4_100,
        },
        {
          id: '20',
          description: 'Base freight, Memphis to Phoenix, part truckload',
          quantity: 4,
          unit: 'load',
          unitPrice: 3_800,
        },
        {
          id: '30',
          description: 'Fuel surcharge (diesel price table, 18% cap)',
          quantity: 1,
          unit: 'lot',
          unitPrice: 8_640,
        },
      ],
    },
    receipt: {
      id: 'POD-2026-0931',
      kind: 'Proof of delivery',
      date: daysAgo(5),
      location: 'Phoenix DC docks 2 and 3',
      quantities: { L1: 8, L2: 4, L3: 1 },
    },
    facts: {},
  },
  'STR-INV-20931': {
    id: 'STR-INV-20931',
    supplierId: 'sup-stratus',
    contractIds: ['EA-STRT-2024-001'],
    channel: 'Supplier portal',
    invoiceDate: daysAgo(8),
    statedTerms: { label: 'Net 45', netDays: 45 },
    reference: 'Billing period September 2026',
    shipTo: HQ,
    letterhead: {
      address: [
        '1200 Cascade Plaza, Floor 14',
        'Seattle, WA 98101',
        'Federal ID 91-2207754',
      ],
      arEmail: 'billing@stratus-cloud.example',
    },
    lines: [
      {
        id: 'L1',
        description: 'Reserved compute commitment, September 2026',
        kind: 'subscription',
        quantity: 1,
        unit: 'month',
        unitPrice: 640_000,
        amount: 640_000,
        poLine: '10',
      },
      {
        id: 'L2',
        description: 'Cloud storage, September 2026',
        kind: 'subscription',
        quantity: 1,
        unit: 'month',
        unitPrice: 210_000,
        amount: 210_000,
        poLine: '20',
      },
      {
        id: 'L3',
        description:
          'Outbound data transfer and enterprise support, September 2026',
        kind: 'subscription',
        quantity: 1,
        unit: 'month',
        unitPrice: 150_000,
        amount: 150_000,
        poLine: '30',
      },
    ],
    po: {
      id: 'PO-4500176',
      lines: [
        {
          id: '10',
          description: 'Reserved compute commitment',
          quantity: 12,
          unit: 'month',
          unitPrice: 640_000,
        },
        {
          id: '20',
          description: 'Cloud storage',
          quantity: 12,
          unit: 'month',
          unitPrice: 210_000,
        },
        {
          id: '30',
          description: 'Outbound data transfer and enterprise support',
          quantity: 12,
          unit: 'month',
          unitPrice: 150_000,
        },
      ],
    },
    receipt: {
      id: 'SES-2026-0912',
      kind: 'Service receipt',
      date: daysAgo(9),
      location: `${O_NEILL}, IT`,
      quantities: { L1: 1, L2: 1, L3: 1 },
    },
    facts: {
      uptime: { period: 'September 2026', pct: 99.81 },
      creditsApplied: 0,
    },
  },
  'KS-104477': {
    id: 'KS-104477',
    supplierId: 'sup-keystone',
    contractIds: ['MSA-KEYS-2024-010'],
    channel: 'Supplier portal',
    invoiceDate: daysAgo(5),
    statedTerms: { label: 'Net 45', netDays: 45 },
    reference: 'Weeks ending Sep 11 to Oct 2, Hayward DC',
    shipTo: HAY,
    letterhead: {
      address: [
        '4400 Camp Bowie Boulevard, Suite 900',
        'Fort Worth, TX 76107',
        'Federal ID 75-2240336',
      ],
      arEmail: 'ar@keystone-staffing.example',
    },
    lines: [
      {
        id: 'L1',
        description: 'Warehouse associate (contract)',
        kind: 'labor',
        quantity: 248,
        unit: 'h',
        unitPrice: 41.45,
        amount: 10_279.6,
        poLine: '10',
        role: 'Warehouse associate',
        payRate: 30,
      },
      {
        id: 'L2',
        description: 'Forklift operator (contract)',
        kind: 'labor',
        quantity: 216,
        unit: 'h',
        unitPrice: 44.25,
        amount: 9_558,
        poLine: '20',
        role: 'Forklift operator',
        payRate: 32,
      },
      {
        id: 'L3',
        description: 'Receiving lead (contract)',
        kind: 'labor',
        quantity: 96,
        unit: 'h',
        unitPrice: 52.4,
        amount: 5_030.4,
        poLine: '30',
        role: 'Receiving lead',
        payRate: 38,
      },
      {
        id: 'L4',
        description: 'Inventory control specialist (contract)',
        kind: 'labor',
        quantity: 300,
        unit: 'h',
        unitPrice: 56.1,
        amount: 16_830,
        poLine: '40',
        role: 'Inventory control specialist',
        payRate: 40.65,
      },
      {
        id: 'L5',
        description: 'Overtime, week ending Sep 25',
        kind: 'overtime',
        quantity: 8,
        unit: 'h',
        unitPrice: 77.5,
        amount: 620,
        evidence: { kind: 'written_approval', onFile: false },
        detail: 'Hours above 40 in the workweek',
      },
    ],
    po: {
      id: 'PO-4500194',
      lines: [
        {
          id: '10',
          description: 'Warehouse associate (contract)',
          quantity: 260,
          unit: 'h',
          unitPrice: 39.6,
        },
        {
          id: '20',
          description: 'Forklift operator (contract)',
          quantity: 220,
          unit: 'h',
          unitPrice: 42.24,
        },
        {
          id: '30',
          description: 'Receiving lead (contract)',
          quantity: 100,
          unit: 'h',
          unitPrice: 50.16,
        },
        {
          id: '40',
          description: 'Inventory control specialist (contract)',
          quantity: 320,
          unit: 'h',
          unitPrice: 53.66,
        },
      ],
    },
    receipt: {
      id: 'TS-KS-2026-39',
      kind: 'Approved timesheets',
      date: daysAgo(6),
      location: `Approved by ${HALE}`,
      quantities: { L1: 248, L2: 216, L3: 96, L4: 300, L5: 8 },
    },
    facts: {},
  },
  'NGF-22871': {
    id: 'NGF-22871',
    supplierId: 'sup-northgate',
    contractIds: ['SA-NGTE-2024-012'],
    channel: 'E-invoice',
    invoiceDate: daysAgo(4),
    statedTerms: { label: 'Net 30', netDays: 30 },
    reference: 'Delivery note NF-558204',
    shipTo: DAL,
    letterhead: {
      address: [
        '2200 Market Parkway',
        'Akron, OH 44311',
        'Federal ID 34-1126680',
      ],
      arEmail: 'ar@northgate-lighting.example',
    },
    lines: [
      {
        id: 'L1',
        description: 'Pillar Pro LED high-bay light fixture, 150 W',
        kind: 'goods',
        quantity: 700,
        unit: 'fixture',
        unitPrice: 89.95,
        amount: 62_965,
        poLine: '10',
      },
      {
        id: 'L2',
        description: 'Hook-and-chain mounting kit for high-bay fixtures',
        kind: 'goods',
        quantity: 100,
        unit: 'kit',
        unitPrice: 12.35,
        amount: 1_235,
        poLine: '20',
      },
    ],
    po: {
      id: 'PO-4500219',
      lines: [
        {
          id: '10',
          description: 'Pillar Pro LED high-bay light fixture, 150 W',
          quantity: 700,
          unit: 'fixture',
          unitPrice: 89.95,
        },
        {
          id: '20',
          description: 'Hook-and-chain mounting kit for high-bay fixtures',
          quantity: 100,
          unit: 'kit',
          unitPrice: 12.35,
        },
      ],
    },
    receipt: {
      id: 'GR-5100880',
      kind: 'Goods receipt',
      date: daysAgo(6),
      location: 'Dallas DC dock 1',
      quantities: { L1: 700, L2: 100 },
    },
    facts: {
      rebate: {
        ytdNetPurchases: 8_240_000,
        creditedYtd: 0,
        basis: 'all_purchases',
      },
    },
  },
  'GAI-2026-0912': {
    id: 'GAI-2026-0912',
    supplierId: 'sup-gently',
    contractIds: ['MSA-GEN-2025-001'],
    channel: 'Supplier portal',
    invoiceDate: daysAgo(7),
    statedTerms: { label: 'Net 45', netDays: 45 },
    reference: 'Billing period September 2026',
    shipTo: HQ,
    letterhead: {
      address: [
        '100 Hudson Street, Suite 400',
        'New York, NY 10013',
        'Federal ID 88-2194430',
      ],
      arEmail: 'billing@gently.example',
    },
    lines: [
      {
        id: 'L1',
        description:
          'Gently AI platform subscription, September 2026 (accounts payable automation and procurement agents)',
        kind: 'subscription',
        quantity: 1,
        unit: 'month',
        unitPrice: 30_000,
        amount: 30_000,
        poLine: '10',
      },
    ],
    po: {
      id: 'PO-4500162',
      lines: [
        {
          id: '10',
          description: 'Platform subscription, contract year 1',
          quantity: 12,
          unit: 'month',
          unitPrice: 30_000,
        },
      ],
    },
    receipt: {
      id: 'SES-2026-0907',
      kind: 'Service receipt',
      date: daysAgo(8),
      location: `${REYES}, Procurement`,
      quantities: { L1: 1 },
    },
    facts: {
      uptime: { period: 'September 2026', pct: 99.97 },
      creditsApplied: 0,
    },
  },
  'AFP-90233': {
    id: 'AFP-90233',
    supplierId: 'sup-aurelia',
    contractIds: ['SA-AURE-2024-009'],
    channel: 'E-invoice',
    invoiceDate: daysAgo(1),
    statedTerms: {
      label: '2% off in 10 days, Net 45',
      netDays: 45,
      discount: { pct: 2, days: 10 },
    },
    reference: 'Delivery note AF-31207',
    shipTo: DAL,
    letterhead: {
      address: [
        'Parque Industrial Mitras, Nave 7',
        'Monterrey, Nuevo Leon 64320, Mexico',
        'RFC ALI9904016K3',
      ],
      arEmail: 'ar@aurelia-lighting.example',
    },
    lines: [
      {
        id: 'L1',
        description: 'Pillar Pro LED high-bay light fixture, 150 W, UL listed',
        kind: 'goods',
        quantity: 2_000,
        unit: 'fixture',
        unitPrice: 88.7,
        amount: 177_400,
        poLine: '10',
      },
      {
        id: 'L2',
        description: 'Pillar Pro carton and label tooling, artwork revision C',
        kind: 'fee',
        quantity: 1,
        unit: 'set',
        unitPrice: 9_000,
        amount: 9_000,
        poLine: '20',
      },
    ],
    po: {
      id: 'PO-4500217',
      lines: [
        {
          id: '10',
          description: 'Pillar Pro LED high-bay light fixture, 150 W',
          quantity: 2_000,
          unit: 'fixture',
          unitPrice: 88.7,
        },
        {
          id: '20',
          description:
            'Pillar Pro carton and label tooling, artwork revision C',
          quantity: 1,
          unit: 'set',
          unitPrice: 9_000,
        },
      ],
    },
    receipt: {
      id: 'GR-5100889',
      kind: 'Goods receipt',
      date: daysAgo(2),
      location: 'Dallas DC dock 1',
      quantities: { L1: 2_000, L2: 1 },
    },
    facts: {},
  },
  'BLC-3318': {
    id: 'BLC-3318',
    supplierId: 'sup-brightline',
    contractIds: ['MSA-BRTL-2025-004'],
    channel: 'Email (PDF)',
    invoiceDate: daysAgo(9),
    statedTerms: { label: 'Net 45', netDays: 45 },
    reference: 'Pillar Pro launch campaign, milestone 2 of 4',
    shipTo: HQ,
    letterhead: {
      address: [
        '875 Lakeshore Plaza, Suite 1200',
        'Chicago, IL 60611',
        'Federal ID 36-4571029',
      ],
      arEmail: 'ar@brightline-creative.example',
    },
    lines: [
      {
        id: 'L1',
        description:
          'Milestone 2: creative concepts and master assets, Pillar Pro launch',
        kind: 'milestone',
        quantity: 1,
        unit: 'milestone',
        unitPrice: 125_000,
        amount: 125_000,
        poLine: '10',
        evidence: {
          kind: 'milestone_acceptance',
          onFile: true,
          ref: 'ACC-2026-0231',
        },
      },
    ],
    po: {
      id: 'PO-4500203',
      lines: [
        {
          id: '10',
          description: 'Milestone 2: creative concepts and master assets',
          quantity: 1,
          unit: 'milestone',
          unitPrice: 125_000,
        },
      ],
    },
    receipt: {
      id: 'ACC-2026-0231',
      kind: 'Milestone acceptance',
      date: daysAgo(10),
      location: `Accepted by ${BROOKS}`,
      quantities: { L1: 1 },
    },
    facts: {},
  },
  'AIG-90412': {
    id: 'AIG-90412',
    supplierId: 'sup-mro-supply',
    contractIds: ['SA-MROS-2025-004'],
    channel: 'Email (PDF)',
    invoiceDate: daysAgo(4),
    statedTerms: {
      label: '2% off in 10 days, Net 30',
      netDays: 30,
      discount: { pct: 2, days: 10 },
    },
    reference: 'Order PO-5582, delivered under bill of lading BOL-44817',
    shipTo: DAL,
    letterhead: {
      address: [
        'Billed by Apex Industrial Group, parent of MRO Supply Co.',
        '2200 Gulf Freeway',
        'Houston, TX 77023',
      ],
      arEmail: 'ar@apexindustrial.com',
    },
    lines: [
      {
        id: 'L1',
        description: 'Unistrut Channel, 1-5/8" x 10 ft',
        kind: 'goods',
        quantity: 120,
        unit: 'length',
        unitPrice: 30,
        amount: 3_600,
        poLine: '10',
        detail: 'Matched to PO line "Strut Channel (12 ga)", same item',
      },
      {
        id: 'L2',
        description: 'Hydraulic Quick Coupler, 1/2" NPT, Flat-Face',
        kind: 'goods',
        quantity: 100,
        unit: 'coupler',
        unitPrice: 24,
        amount: 2_400,
        poLine: '20',
      },
      {
        id: 'L3',
        description: 'Galvanized Mounting Bracket, Heavy Duty',
        kind: 'goods',
        quantity: 80,
        unit: 'bracket',
        unitPrice: 22.5,
        amount: 1_800,
        poLine: '30',
      },
      {
        id: 'L4',
        description: 'Brass Ball Valve, 1" NPT',
        kind: 'goods',
        quantity: 80,
        unit: 'valve',
        unitPrice: 25,
        amount: 2_000,
        poLine: '40',
      },
    ],
    po: {
      id: 'PO-5582',
      lines: [
        {
          id: '10',
          description: 'Strut Channel (12 ga), 1-5/8" x 10 ft',
          quantity: 120,
          unit: 'length',
          unitPrice: 30,
        },
        {
          id: '20',
          description: 'Hydraulic Quick Coupler, 1/2" NPT, Flat-Face',
          quantity: 100,
          unit: 'coupler',
          unitPrice: 24,
        },
        {
          id: '30',
          description: 'Galvanized Mounting Bracket, Heavy Duty',
          quantity: 80,
          unit: 'bracket',
          unitPrice: 22.5,
        },
        {
          id: '40',
          description: 'Brass Ball Valve, 1" NPT',
          quantity: 80,
          unit: 'valve',
          unitPrice: 25,
        },
      ],
    },
    receipt: {
      id: 'POD-44817',
      kind: 'Proof of delivery',
      date: daysAgo(6),
      location: 'Dallas DC receiving',
      quantities: { L1: 120, L2: 100, L3: 80, L4: 80 },
    },
    facts: {},
    apInvoiceId: AP_DEMO_IDS.invoiceAtl,
  },
  'BS-22188': {
    id: 'BS-22188',
    supplierId: 'sup-border-states',
    contractIds: ['SA-BRST-2024-011'],
    channel: 'Email (PDF)',
    invoiceDate: daysAgo(9),
    statedTerms: { label: 'Net 30', netDays: 30 },
    reference: 'Delivery against PO-5571',
    shipTo: DAL,
    letterhead: {
      address: [
        '1100 Commerce Street',
        'San Antonio, TX 78205',
        'Federal ID 74-2210981',
      ],
      arEmail: 'billing@borderstates.com',
    },
    lines: [
      {
        id: 'L1',
        description: 'EMT Conduit, 3/4" x 10 ft',
        kind: 'goods',
        quantity: 60,
        unit: 'length',
        unitPrice: 28,
        amount: 1_680,
        poLine: '10',
      },
      {
        id: 'L2',
        description: 'Weatherproof Junction Box, 1-Gang, Cast Aluminum',
        kind: 'goods',
        quantity: 50,
        unit: 'box',
        unitPrice: 25,
        amount: 1_250,
        poLine: '20',
      },
      {
        id: 'L3',
        description: 'Liquidtight Connector, 3/4" Straight',
        kind: 'goods',
        quantity: 80,
        unit: 'connector',
        unitPrice: 16.5,
        amount: 1_320,
        poLine: '30',
      },
    ],
    po: {
      id: 'PO-5571',
      lines: [
        {
          id: '10',
          description: 'EMT Conduit, 3/4" x 10 ft',
          quantity: 60,
          unit: 'length',
          unitPrice: 28,
        },
        {
          id: '20',
          description: 'Weatherproof Junction Box, 1-Gang, Cast Aluminum',
          quantity: 50,
          unit: 'box',
          unitPrice: 25,
        },
        {
          id: '30',
          description: 'Liquidtight Connector, 3/4" Straight',
          quantity: 80,
          unit: 'connector',
          unitPrice: 16.5,
        },
      ],
    },
    receipt: {
      id: 'GR-5100840',
      kind: 'Goods receipt',
      date: daysAgo(11),
      location: 'Dallas DC dock 3',
      quantities: { L1: 60, L2: 50, L3: 80 },
    },
    facts: {},
    apInvoiceId: AP_DEMO_IDS.invoiceFreshLink,
  },
  'BVL-7741': {
    id: 'BVL-7741',
    supplierId: 'sup-brazos',
    contractIds: ['MSA-BRZV-2025-002'],
    channel: 'Email (PDF)',
    invoiceDate: daysAgo(1),
    statedTerms: { label: 'Net 45', netDays: 45 },
    reference: 'Warehouse services against PO-7740',
    shipTo: 'Bryan, TX (Brazos Valley warehouse)',
    letterhead: {
      address: [
        '720 Industrial Park Drive',
        'Bryan, TX 77803',
        'Federal ID 74-3092271',
      ],
      arEmail: 'accounts@brazosvalleylogistics.com',
    },
    lines: [
      {
        id: 'L1',
        description: 'Inbound Pallet Handling (in/out)',
        kind: 'goods',
        quantity: 40,
        unit: 'pallet',
        unitPrice: 18,
        amount: 720,
        poLine: '10',
      },
      {
        id: 'L2',
        description: 'Outbound Crating & Export Packaging',
        kind: 'accessorial',
        quantity: 1,
        unit: 'job',
        unitPrice: 600,
        amount: 600,
        evidence: {
          kind: 'signed_proof',
          onFile: true,
          ref: 'WO-77402',
        },
        detail: 'Not on PO-7740; signed work order WO-77402 on file',
      },
    ],
    po: {
      id: 'PO-7740',
      lines: [
        {
          id: '10',
          description: 'Pallet Handling (in/out), per pallet',
          quantity: 40,
          unit: 'pallet',
          unitPrice: 18,
        },
        {
          id: '20',
          description: 'Ambient Storage, per pallet/month',
          quantity: 10,
          unit: 'pallet-month',
          unitPrice: 12,
        },
      ],
    },
    receipt: {
      id: 'RCV-7740',
      kind: 'Goods receipt',
      date: daysAgo(2),
      location: 'Bryan warehouse',
      quantities: { L1: 40, L2: 1 },
    },
    facts: {},
    apInvoiceId: AP_DEMO_IDS.invoiceValleyCold,
  },
  'SF-3398': {
    id: 'SF-3398',
    supplierId: 'sup-sentinel',
    contractIds: ['MSA-SNTL-2025-003'],
    channel: 'Email (PDF)',
    invoiceDate: daysAgo(3),
    statedTerms: { label: 'Net 15', netDays: 15 },
    reference: 'Freight for PO-5582, shipment SHP-44817',
    shipTo: DAL,
    letterhead: {
      address: [
        '1800 Freight Terminal Road',
        'Irving, TX 75061',
        'Federal ID 75-2981140',
      ],
      arEmail: 'ap@sentinelfreight.com',
    },
    lines: [
      {
        id: 'L1',
        description: 'Part-truckload freight, PO-5582 delivery',
        kind: 'line_haul',
        quantity: 1,
        unit: 'shipment',
        unitPrice: 1_200,
        amount: 1_200,
        poLine: '10',
      },
    ],
    po: {
      id: 'SHP-44817',
      lines: [
        {
          id: '10',
          description: 'Houston to Dallas DC, 18,450 lb',
          quantity: 1,
          unit: 'shipment',
          unitPrice: 1_200,
        },
      ],
    },
    receipt: {
      id: 'POD-44817',
      kind: 'Proof of delivery',
      date: daysAgo(6),
      location: 'Dallas DC receiving',
      quantities: { L1: 1 },
    },
    facts: {},
    apInvoiceId: AP_DEMO_IDS.invoiceFreight,
  },
};

export const INVOICE_LIST: Invoice[] = INVOICE_IDS.map((id) => INVOICES[id]);

export function getInvoice(id: InvoiceId): Invoice {
  return INVOICES[id];
}
